import { styled } from '@compiled/react';
import type { VFC } from 'react';
import React, { useEffect } from 'react';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { ConfluenceLogo, ConfluenceIcon } from '@atlaskit/logo';
import { N0, N60 } from '@atlaskit/theme/colors';
import { layers } from '@atlaskit/theme/constants';
import { token } from '@atlaskit/tokens';

import { BannerStateContainer } from '@confluence/banners';
import { Attribution, TransparentErrorBoundary } from '@confluence/error-boundary';
import { gridSize } from '@confluence/typography-placeholder';
import { fg } from '@confluence/feature-gating';

import type { UserTypes } from '../constants';
import { ANALYTICS_SOURCE, LEARN_MORE_URL } from '../constants';
import { ConditionalUserButtons } from '../buttons/ConditionalUserButtons';

const BANNER_HEIGHT = gridSize * 6;
const BANNER_NAME = 'RSUBanner';
const RENDER_LOCATION = 'banner';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	position: 'fixed',
	top: 0,
	left: 0,
	right: 0,
	zIndex: layers.navigation(),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${BANNER_HEIGHT}px`,
	borderBottom: `1px solid ${token('color.border', N60)}`,
	boxShadow: token('elevation.shadow.overflow', `0 4px 4px -4px ${N60}`),
	background: token('elevation.surface', N0),
	display: 'flex',
	marginBottom: token('space.200', '16px'),
	alignItems: 'center',
	justifyContent: 'center',
	overflow: 'hidden',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LogoLinkContainer = styled.a({
	display: 'inline-flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SideContainer = styled.div({
	padding: `${token('space.100', '8px')} ${token('space.200', '16px')}`,
	display: 'flex',
	verticalAlign: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MultiButtonContainer = styled.div<{ isMobileView: boolean }>({
	flexGrow: 1,
	display: 'flex',
	justifyContent: 'flex-end',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	padding: ({ isMobileView }) =>
		isMobileView
			? `${token('space.075', '6px')} ${token('space.250', '20px')} ${token(
					'space.075',
					'6px',
				)} ${token('space.075', '6px')}`
			: token('space.075', '6px'),
});

type ExternalPageBannerWithBannerStateProps = ExternalPageBannerProps & {
	bannerState: BannerStateContainer;
};

const ExternalPagerBannerWithBannerState: VFC<ExternalPageBannerWithBannerStateProps> = ({
	bannerState,
	contentId,
	shareToken,
	cloudId,
	spaceKey,
	userType,
	isMobileView,
	contentType,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isContentIdRemovalEnabled = fg('public_links_content_id_removal');

	const createOnClickHandler = (actionSubjectId: string) => () =>
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				source: ANALYTICS_SOURCE,
				actionSubjectId,
				attributes: {
					pageId: isContentIdRemovalEnabled ? shareToken : contentId,
					location: RENDER_LOCATION,
				},
			},
		}).fire();

	useEffect(() => {
		bannerState.show(BANNER_NAME, BANNER_HEIGHT);
		return () => {
			bannerState.hide(BANNER_NAME);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'rendered',
				actionSubject: RENDER_LOCATION,
				source: ANALYTICS_SOURCE,
				actionSubjectId: BANNER_NAME,
			},
		}).fire();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Container>
			<SideContainer>
				<LogoLinkContainer
					href={LEARN_MORE_URL}
					onClick={createOnClickHandler('confluenceLogo')}
					target="_blank"
				>
					{isMobileView ? (
						<ConfluenceIcon appearance="brand" />
					) : (
						<ConfluenceLogo appearance="brand" />
					)}
				</LogoLinkContainer>
			</SideContainer>
			<MultiButtonContainer isMobileView={isMobileView}>
				<ConditionalUserButtons
					userType={userType}
					contentId={contentId}
					cloudId={cloudId}
					spaceKey={spaceKey}
					renderLocation={RENDER_LOCATION}
					shortenTryButton={isMobileView}
					contentType={contentType}
					shareToken={shareToken}
					isContentIdRemovalEnabled={isContentIdRemovalEnabled}
				/>
			</MultiButtonContainer>
		</Container>
	);
};

export type ExternalPageBannerProps = {
	contentId: string;
	cloudId: string;
	spaceKey?: string | null;
	shareToken: string;
	userType: UserTypes;
	isMobileView: boolean;
	contentType?: string | null;
};

export const ExternalPageBanner: VFC<ExternalPageBannerProps> = (props) => (
	<TransparentErrorBoundary attribution={Attribution.EXTERNAL_COLLAB}>
		<Subscribe to={[BannerStateContainer]}>
			{(bannerState: BannerStateContainer) => (
				<ExternalPagerBannerWithBannerState {...props} bannerState={bannerState} />
			)}
		</Subscribe>
	</TransparentErrorBoundary>
);
